// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCreditNotesStore } from '../../../stores/creditNotesStore';
import { useInvoicesStore } from '../../../stores/invoicesStore';
import { Button } from '@/components/ui-v2';
import CreditNoteEditTemplate from '@/components/templates/credit-note/CreditNoteEditTemplate';
import CreditNoteViewTemplate from '@/components/templates/credit-note/CreditNoteViewTemplate';
import { FileText, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';

// Loading Spinner Component
const LoadingState: React.FC<{ message: string }> = ({ message }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-lg p-6 text-center">
      <div className="flex flex-col items-center gap-4">
        <div className="p-4 bg-teal-100 rounded-2xl">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
        <p className="text-gray-700 font-medium">{message}</p>
      </div>
    </div>
  </div>
);

// Error State Component
const ErrorState: React.FC<{
  title: string;
  message?: string;
  onBack: () => void;
  backLabel: string;
}> = ({ title, message, onBack, backLabel }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-lg p-6 text-center max-w-md">
      <div className="flex flex-col items-center gap-4">
        <div className="p-4 bg-red-100 rounded-2xl">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <div>
          <p className="text-gray-900 font-semibold text-lg">{title}</p>
          {message && (
            <p className="text-sm text-gray-500 mt-2">{message}</p>
          )}
        </div>
        <Button
          onClick={onBack}
          className="mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </Button>
      </div>
    </div>
  </div>
);

// Wrapper pour créer un nouvel avoir à partir d'une facture
export const CreditNoteNewRoute: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get('invoiceId');

  // Charger la facture source
  const { currentInvoice, isLoading, error, fetchInvoice } = useInvoicesStore();

  React.useEffect(() => {
    if (invoiceId) {
      fetchInvoice(invoiceId);
    }
  }, [invoiceId, fetchInvoice]);

  if (!invoiceId) {
    return (
      <ErrorState
        title={t('billing.creditNotes.routes.noInvoiceSelected')}
        message={t('billing.creditNotes.routes.selectInvoiceToCreate')}
        onBack={() => navigate('/credit-notes')}
        backLabel={t('billing.creditNotes.routes.backToList')}
      />
    );
  }

  if (isLoading) {
    return <LoadingState message={t('billing.creditNotes.routes.loadingInvoice')} />;
  }

  if (error || !currentInvoice) {
    return (
      <ErrorState
        title={t('billing.creditNotes.routes.invoiceNotFound')}
        message={t('billing.creditNotes.routes.invoiceNotFoundMessage')}
        onBack={() => navigate('/credit-notes')}
        backLabel={t('billing.creditNotes.routes.backToList')}
      />
    );
  }

  return (
    <CreditNoteEditTemplate
      invoiceData={currentInvoice}
      onSuccess={() => navigate('/credit-notes')}
      onCancel={() => navigate('/credit-notes')}
    />
  );
};

// Wrapper pour éditer un avoir existant
export const CreditNoteEditRoute: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { currentCreditNote, isLoading, error, fetchCreditNote } = useCreditNotesStore();

  React.useEffect(() => {
    if (id && id !== '0') {
      fetchCreditNote(id);
    }
  }, [id, fetchCreditNote]);

  if (isLoading) {
    return <LoadingState message={t('billing.creditNotes.routes.loadingCreditNote')} />;
  }

  if (error || !currentCreditNote) {
    return (
      <ErrorState
        title={t('billing.creditNotes.routes.creditNoteNotFound')}
        onBack={() => navigate('/credit-notes')}
        backLabel={t('billing.creditNotes.routes.backToList')}
      />
    );
  }

  // Pour l'instant, rediriger vers la vue car l'édition d'avoir n'est peut-être pas nécessaire
  // (les avoirs sont souvent immuables une fois créés)
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-6 text-center max-w-md">
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-amber-100 rounded-2xl">
            <FileText className="w-8 h-8 text-amber-600" />
          </div>
          <div>
            <p className="text-gray-900 font-semibold text-lg">
              {t('billing.creditNotes.routes.editUnavailable')}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {t('billing.creditNotes.routes.editUnavailableMessage')}
            </p>
          </div>
          <Button
            onClick={() => navigate('/credit-notes')}
            className="mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('billing.creditNotes.routes.backToList')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Wrapper pour voir un avoir en lecture seule
export const CreditNoteViewRoute: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { currentCreditNote, isLoading, error, fetchCreditNote } = useCreditNotesStore();

  React.useEffect(() => {
    if (id && id !== '0') {
      fetchCreditNote(id);
    }
  }, [id, fetchCreditNote]);

  if (isLoading) {
    return <LoadingState message={t('billing.creditNotes.routes.loadingCreditNote')} />;
  }

  if (error || !currentCreditNote) {
    return (
      <ErrorState
        title={t('billing.creditNotes.routes.creditNoteNotFound')}
        onBack={() => navigate('/credit-notes')}
        backLabel={t('billing.creditNotes.routes.backToList')}
      />
    );
  }

  return (
    <CreditNoteViewTemplate
      creditNoteData={currentCreditNote}
      onClose={() => navigate('/credit-notes')}
    />
  );
};
